import React, { useCallback, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

const BottomSheetOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  /* Remove backdrop-filter for better mobile performance */
  /* Hardware acceleration for smooth overlay animation */
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
`;

const BottomSheet = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  z-index: 1000;
  padding: 2rem 1.5rem 3rem 1.5rem;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  /* Optimized for smooth animations */
  will-change: transform;
  backface-visibility: hidden;
  /* Prevent layout thrashing */
  contain: layout style paint;
  /* Mobile touch optimizations */
  touch-action: pan-y;
  /* Separate hardware acceleration from animation transform */
  transform: translate3d(0, 0, 0);
`;

const HandleBar = styled.div`
  width: 40px;
  height: 4px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
  margin: 0 auto 1.5rem auto;
`;

const Content = styled.div`
  text-align: center;
  padding: 0 1rem;
  width: 100%;
  box-sizing: border-box;

  h2 {
    font-size: 24px;
    font-weight: 900;
    margin-bottom: 1rem;
    font-style: italic;
    color: #000;
    line-height: 1.2;
    letter-spacing: -0.02em;
    font-family: "Inter", sans-serif;

    @media (max-width: 480px) {
      font-size: 20px;
    }

    @media (max-width: 360px) {
      font-size: 18px;
    }
  }

  p {
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 1.5rem;
    color: rgba(0, 0, 0, 0.8);
    font-weight: 500;
    font-family: "Inter", sans-serif;

    @media (max-width: 480px) {
      font-size: 13px;
    }

    @media (max-width: 360px) {
      font-size: 12px;
    }
  }
`;

/* eslint-disable-next-line no-unused-vars */
const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
`;

/* eslint-disable-next-line no-unused-vars */
const QRCodeImage = styled.img`
  width: 180px;
  height: auto;
  max-width: 100%;

  @media (max-width: 480px) {
    width: 160px;
  }

  @media (max-width: 360px) {
    width: 140px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 18px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  z-index: 1001;
  pointer-events: auto;
  /* Add hardware acceleration */
  will-change: transform;
  transform: translateZ(0);

  &:hover {
    background: rgba(0, 0, 0, 0.15);
  }

  &:active {
    background: rgba(0, 0, 0, 0.2);
    transform: translateZ(0) scale(0.95);
  }
`;

const DownloadButton = styled.button`
  border: 1px solid #00ff00;
  box-shadow: 0 4px 0 #00ff00;
  background: white;
  font-weight: 900;
  font-family: "Inter", sans-serif;
  font-size: 1rem;
  width: calc(100% - 2rem); /* Account for horizontal margins */
  margin: 1rem;
  color: black;
  padding: 1rem;
  border-radius: 1rem;
  cursor: pointer;

  &:hover {
    background: #f0f0f0;
  }

  &:active {
    transform: translateY(2px);
    box-shadow: 0 2px 0 #00ff00;
  }
`;

// Define animation variants outside component to prevent re-creation
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const sheetVariants = {
  hidden: {
    y: "100%",
    transition: {
      type: "tween",
      ease: [0.32, 0.72, 0, 1],
      duration: 0.4,
    },
  },
  visible: {
    y: 0,
    transition: {
      type: "tween",
      ease: [0.32, 0.72, 0, 1],
      duration: 0.5,
    },
  },
  exit: {
    y: "100%",
    transition: {
      type: "tween",
      ease: [0.32, 0.72, 0, 1],
      duration: 0.3,
    },
  },
};

const overlayTransition = {
  duration: 0.3,
  ease: "easeOut",
};

const MobileBottomSheet = ({
  isOpen,
  onClose,
  title,
  content,
  imageUrl,
  showDownloadButton = true,
}) => {
  // Stabilize viewport during animation to prevent jank
  useEffect(() => {
    if (isOpen) {
      // Prevent viewport changes during animation - but don't disable touch entirely
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";

      // Only prevent zoom, not all touch interactions
      const preventZoom = e => {
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      };

      document.addEventListener("touchstart", preventZoom, { passive: false });

      return () => {
        document.body.style.overflow = originalStyle;
        document.removeEventListener("touchstart", preventZoom);
      };
    }
  }, [isOpen]);

  // Memoize click handlers to prevent re-renders
  const handleOverlayClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSheetClick = useCallback(e => {
    e.stopPropagation();
  }, []);

  const handleCloseClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          <BottomSheetOverlay
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={overlayTransition}
            onClick={handleOverlayClick}
          />
          <BottomSheet
            variants={sheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleSheetClick}
          >
            <HandleBar />
            <CloseButton onClick={handleCloseClick}>×</CloseButton>
            <Content>
              <h2>THE HITCHHIKER’S GUIDE TO THE GALAXY</h2>
              <p>
                In The Hitchhiker’s Guide to the Galaxy, “42” is the “Answer to
                the Ultimate Question of Life, the Universe, and Everything,”
                calculated by the supercomputer Deep Thought over 7.5 million
                years. However, the actual question is unknown, making the
                answer meaningless. Douglas Adams, the author, later explained
                that he chose 42 simply because it was a “funny, ordinary
                number” with no deeper significance.
              </p>
            </Content>
            <DownloadButton>Download App</DownloadButton>
          </BottomSheet>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileBottomSheet;
